import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';

// Shared styling for the public-facing document-share pages (email gate, magic-link
// view, and public view). Mirrors the compliance-manager-app policy/procedure detail
// look: a doc-icon hero (uppercase eyebrow + bold title + version chip) above section
// cards with a primary-coloured header bar. Primary (#223d79) matches CMA's theme.
export const DOC_ICON_BG = '#eeedfe';
export const DOC_ICON_FG = '#534ab7';

export const useDocumentShareStyles = makeStyles((theme: Theme) =>
    createStyles({
        page: {
            paddingTop: theme.spacing(4),
            paddingBottom: theme.spacing(7),
        },
        wrap: {
            justifyContent: 'center',
        },
        stack: {
            width: '100%',
            maxWidth: 680,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
        },
        center: {
            textAlign: 'center',
            padding: theme.spacing(8, 2),
        },

        // Hero (doc identity)
        hero: {
            padding: theme.spacing(3),
            borderRadius: 8,
            borderColor: theme.palette.grey[300],
        },
        heroRow: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: theme.spacing(2.5),
        },
        heroAvatar: {
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: DOC_ICON_BG,
            color: DOC_ICON_FG,
            flexShrink: 0,
        },
        heroBody: { flex: 1, minWidth: 0 },
        eyebrow: {
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 1.2,
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(0.5),
        },
        heroTitle: {
            fontSize: 26,
            fontWeight: 700,
            margin: 0,
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
            color: theme.palette.text.primary,
        },
        metaRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            flexWrap: 'wrap',
            marginTop: theme.spacing(1.5),
        },
        versionChip: {
            background: theme.palette.grey[100],
            color: theme.palette.text.secondary,
            fontFamily: 'ui-monospace, Menlo, monospace',
            fontWeight: 600,
        },
        lead: {
            color: theme.palette.text.secondary,
            fontSize: 14,
            lineHeight: 1.55,
            marginTop: theme.spacing(0.5),
        },

        // Section card with primary header bar
        sectionCard: {
            borderRadius: 8,
            borderColor: theme.palette.grey[300],
            overflow: 'hidden',
        },
        panelHeaderBar: {
            background: theme.palette.primary.main,
            color: '#fff',
            padding: theme.spacing(1.5, 2.5),
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        panelHeading: {
            fontSize: 15,
            fontWeight: 600,
            color: '#fff',
            margin: 0,
        },
        // Redesigned export panel: micro label, segmented style toggle + Download,
        // file-info card, and framed preview.
        exportStyleLabel: {
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(1),
        },
        exportToolbar: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(2),
            flexWrap: 'wrap',
            marginBottom: theme.spacing(2),
        },
        exportToggle: {
            display: 'inline-flex',
            gap: 4,
            padding: 4,
            background: theme.palette.grey[100],
            borderRadius: 10,
        },
        exportToggleBtn: {
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            font: 'inherit',
            fontSize: 14,
            fontWeight: 600,
            color: theme.palette.text.secondary,
            padding: theme.spacing(1, 2),
            borderRadius: 8,
            transition: 'background 0.15s, color 0.15s, box-shadow 0.15s',
        },
        exportToggleBtnActive: {
            background: '#fff',
            color: theme.palette.primary.main,
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        },
        exportActions: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
        },
        fileCard: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            padding: theme.spacing(1.5, 2),
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 10,
            marginBottom: theme.spacing(2),
        },
        fileCardIcon: {
            width: 40,
            height: 40,
            borderRadius: 8,
            background: DOC_ICON_BG,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        fileCardBody: { flex: 1, minWidth: 0 },
        fileCardName: {
            fontSize: 15,
            fontWeight: 700,
            color: theme.palette.text.primary,
            lineHeight: 1.2,
        },
        fileCardMeta: {
            fontSize: 12.5,
            color: theme.palette.text.secondary,
            marginTop: 2,
        },
        brandedChip: {
            background: DOC_ICON_BG,
            color: DOC_ICON_FG,
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: 0.5,
        },
        originalChip: {
            background: theme.palette.grey[100],
            color: theme.palette.text.secondary,
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: 0.5,
        },
        previewWrap: {
            background: theme.palette.grey[100],
            borderRadius: 10,
            padding: theme.spacing(2),
        },
        brandedPreviewLoading: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: theme.spacing(4, 2),
            color: theme.palette.text.secondary,
        },
        panelBody: {
            padding: theme.spacing(2.5),
        },

        // Message
        message: {
            whiteSpace: 'pre-wrap',
            background: theme.palette.grey[50],
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 8,
            padding: theme.spacing(1.5, 2),
            fontSize: 14,
            lineHeight: 1.55,
        },

        // Attachments
        attachmentBlock: {
            padding: theme.spacing(1.5, 0),
            borderTop: `1px solid ${theme.palette.divider}`,
            '&:first-child': { borderTop: 'none' },
        },
        attachmentRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(1.5),
        },
        attachmentName: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            minWidth: 0,
        },
        attachmentIcon: { color: theme.palette.text.secondary, flexShrink: 0 },
        previewImage: {
            display: 'block',
            maxWidth: '100%',
            maxHeight: 480,
            marginTop: theme.spacing(1.5),
            borderRadius: 6,
            border: `1px solid ${theme.palette.grey[200]}`,
        },
        previewFrame: {
            display: 'block',
            width: '100%',
            height: 600,
            marginTop: theme.spacing(1.5),
            borderRadius: 6,
            border: `1px solid ${theme.palette.grey[200]}`,
        },
        emptyHint: {
            fontSize: 13.5,
            color: theme.palette.text.secondary,
        },

        // Details
        detailGroup: {
            marginBottom: theme.spacing(2),
            '&:last-child': { marginBottom: 0 },
        },
        detailLabel: {
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(0.5),
        },
        detailText: {
            fontSize: 14,
            lineHeight: 1.55,
            color: theme.palette.text.primary,
            whiteSpace: 'pre-wrap',
        },

        // Acknowledgement
        ackIntro: {
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(2),
        },
        confirmBox: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: theme.spacing(1),
            padding: theme.spacing(1.5, 2),
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: 8,
            margin: theme.spacing(2, 0),
        },
        confirmBoxChecked: {
            borderColor: theme.palette.primary.main,
            background: 'rgba(34,61,121,0.04)',
        },
        // Green call-to-action buttons (Send secure link / Acknowledge / Download).
        // #92b765 matches the rest of the app's primary action green.
        greenButton: {
            backgroundColor: '#92b765',
            color: '#fff',
            '&:hover': { backgroundColor: '#80a159' },
        },
        greenTextButton: {
            color: '#6f9544',
        },
        buttonWrapper: {
            position: 'relative',
            display: 'inline-block',
        },
        buttonProgress: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginLeft: -12,
            marginTop: -12,
        },
    }),
);
